import {
  EditOutlined,
  YoutubeOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import { Table, Button, Space, Tag, Tooltip, Popconfirm } from "antd";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isBetween);

const ContestTable = ({
  contests = [],
  pagination = {},
  currentPage = 1,
  pageSize = 10,
  isLoading = false,
  onPageChange = () => {},
  onEditContest = () => {},
  onDeleteContest = () => {},
  isDeletingContest = false,
  userConfig = null
}) => {
  const formatDuration = (seconds) => {
    // Handle empty or zero values
    if (!seconds || seconds < 1) return "0 min";

    const dur = dayjs.duration(seconds, "seconds");
    const hours = Math.floor(dur.asHours());
    const minutes = dur.minutes();

    // If it's less than 60 seconds, dur.minutes() is 0.
    // You might want to show "1 min" as a minimum floor.
    const displayMinutes = hours === 0 && minutes === 0 && seconds > 0 ? 1 : minutes;

    if (hours === 0) {
      return `${displayMinutes} min`;
    } else if (displayMinutes === 0) {
      return `${hours} hr`;
    } else {
      return `${hours} hr ${displayMinutes} min`;
    }
  };

  const formatDateInIST = (date) => {
    // This automatically handles the offset for India
    return dayjs(date).tz("Asia/Kolkata").format("DD MMM YYYY, HH:mm");
  };

  const columns = [
    {
      title: "Contest Name",
      dataIndex: "name",
      key: "name",
      width: "18%",
      align: "center",
      render: (text, record) => (
        <Tooltip title={text}>
          <a
            href={record.link}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontWeight: "500",
              cursor: "pointer",
              color: "#1890ff",
              textDecoration: "underline"
            }}
          >
            {text}
          </a>
        </Tooltip>
      )
    },
    {
      title: "Contest ID",
      dataIndex: "contestIdentifier",
      key: "contestIdentifier",
      width: "10%",
      align: "center"
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      width: "8%",
      align: "center",
      render: (platform) => <Tag color="blue">{platform}</Tag>
    },
    {
      title: "Start Date (IST)",
      dataIndex: "startDate",
      key: "startDate",
      width: "12%",
      align: "center",
      render: (date) => formatDateInIST(date),
      sorter: (a, b) => dayjs(a.startDate).unix() - dayjs(b.startDate).unix()
    },
    {
      title: "End Date (IST)",
      dataIndex: "endDate",
      key: "endDate",
      width: "12%",
      align: "center",
      render: (date) => formatDateInIST(date)
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      width: "7%",
      align: "center",
      render: (duration) => formatDuration(duration)
    },
    {
      title: "Status",
      key: "status",
      width: "7%",
      align: "center",
      render: (_, record) => {
        const now = dayjs().utcOffset("+05:30");
        const start = dayjs(record.startDate).utcOffset("+05:30");
        const end = dayjs(record.endDate).utcOffset("+05:30");

        let status, color;
        if (now.isBefore(start)) {
          status = "Upcoming";
          color = "blue";
        } else if (now.isBetween(start, end)) {
          status = "Live";
          color = "green";
        } else {
          status = "Ended";
          color = "default";
        }

        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: "Solution",
      key: "solution",
      width: "7%",
      align: "center",
      render: (_, record) => {
        if (record?.solutionVideoInfo?.link) {
          return (
            <Button
              type="link"
              onClick={() => window.open(record.solutionVideoInfo.link, "_blank")}
              style={{ color: "#ff4d4f", padding: 0 }}
            >
              <YoutubeOutlined style={{ fontSize: "32px" }} />
            </Button>
          );
        }
        return (
          <CloseOutlined
            style={{
              fontSize: "16px",
              color: "#ff4d4f"
            }}
          />
        );
      }
    },
    {
      title: "Timestamps",
      key: "timestamps",
      width: "9%",
      align: "center",
      render: (_, record) => {
        const hasProblemSolutions = record.solutionVideoInfo?.problemSolutions?.length > 0;

        if (hasProblemSolutions) {
          return (
            <CheckOutlined
              style={{
                fontSize: "16px",
                color: "#52c41a"
              }}
            />
          );
        }
        return (
          <CloseOutlined
            style={{
              fontSize: "16px",
              color: "#ff4d4f"
            }}
          />
        );
      }
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          {userConfig?.globalContests?.hasModifyAccess && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEditContest(record)}
              >
                Edit
              </Button>
              <Popconfirm
                title="Delete Contest"
                description="Are you sure you want to delete this contest?"
                onConfirm={() => onDeleteContest(record.contestIdentifier)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={isDeletingContest}
                >
                  Delete
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={contests}
      loading={isLoading}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        total: pagination.totalItems,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contests`,
        pageSizeOptions: ["10", "20", "50", "100"],
        onChange: onPageChange,
        onShowSizeChange: onPageChange
      }}
      scroll={{ x: 1200 }}
      bordered
    />
  );
};

export default ContestTable;
